// ============================================================
// NotificationBell — header 通知入口：显示未读数并提示最近通知。
// 未读数由 useInboxUnreadCountQuery 30s 轮询驱动，用户无需进入
// Inbox 页面即可感知新通知；点击进入收件箱。
// ============================================================

import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Bell, Mail } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useNotification } from '@/components/ui/notification-context';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useInboxUnreadCountQuery } from '@/features/inbox/hooks/use-inbox-queries';
import { cn } from '@/lib/utils';

export function NotificationBell({ className }: { className?: string }) {
  const { t } = useTranslation('shell');
  const { tenantId = '' } = useParams({ strict: false });
  const navigate = useNavigate();
  const notification = useNotification();
  const { can } = useAuth();
  const canReadInbox = can('inbox.read', { tenantId });
  const unreadQuery = useInboxUnreadCountQuery({ enabled: canReadInbox });
  const unreadCount = unreadQuery.data?.unreadCount ?? 0;
  const latestNotification = unreadQuery.data?.latestNotifications[0];

  useEffect(() => {
    if (!latestNotification) return;

    const body = latestNotification.body?.trim();
    const bodyPreview =
      body && body.length > 120 ? `${body.slice(0, 120)}…` : body;
    notification.info({
      key: `inbox-${latestNotification.id}`,
      title: latestNotification.title,
      description: bodyPreview
        ? `来自 ${latestNotification.senderName}：${bodyPreview}`
        : `来自 ${latestNotification.senderName}`,
      icon: <Mail className="size-5" aria-hidden="true" />,
      placement: 'topRight',
      closable: true,
      onClick: () => {
        void navigate({
          to: '/w/$tenantId/inbox/$messageId',
          params: { tenantId, messageId: latestNotification.id },
        });
      },
    });
  }, [latestNotification, navigate, notification, tenantId]);

  if (!canReadInbox) return null;

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      render={<Link to="/w/$tenantId/inbox" params={{ tenantId }} />}
      nativeButton={false}
      aria-label={
        unreadCount > 0
          ? t('unreadNotifications', { count: unreadCount })
          : t('notifications')
      }
      className={cn('relative', className)}
    >
      <Bell className="size-4" aria-hidden="true" />
      {unreadCount > 0 ? (
        <span
          className="absolute -right-1 -top-1 grid min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-4 text-destructive-foreground ring-2 ring-background"
          aria-hidden="true"
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      ) : null}
    </Button>
  );
}
